import React from "react";
import { Button, Popover } from "antd";
import { useNavigate } from "react-router-dom";
import { RiShutDownLine, RiUser3Line } from "react-icons/ri";

import { PLAN_NAMES } from "../../constants/PricingConstants";

function formatExpiry(expiry) {
  if (!expiry) return "—";

  try {
    return new Date(expiry).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return expiry;
  }
}

function formatPlanLabel(plan) {
  if (!plan) return "No Plan";
  return PLAN_NAMES[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
}

export default function ProfilePopover({
  open,
  onOpenChange,
  userProfile,
  onLogout,
  onOpenTemplates,
  isWabaConnected = false,
  isSubscriptionValid = false,
}) {
  const navigate = useNavigate();
  const planData = userProfile?.Plan
    ? {
        plan: (userProfile.Plan.plan || userProfile.Plan.Plan || "").toLowerCase(),
        expiry: userProfile.Plan.expiry || userProfile.Plan.Expiry || "",
      }
    : { plan: "", expiry: "" };


  const handleManageSubscription = () => {
    if (!isWabaConnected) return;
    onOpenChange(false);
    navigate("/subscription");
  };

  const handleManageTemplates = () => {
    if (!isWabaConnected || !isSubscriptionValid) return;
    onOpenChange(false);
    onOpenTemplates?.();
  };

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>My Profile</span>
          <button
            type="button"
            onClick={() => onLogout?.()}
            style={{
              border: "none",
              background: "transparent",
              color: "#c62828",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: "4px",
              borderRadius: "4px",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.background = "rgba(198, 40, 40, 0.08)")
            }
            onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
            title="Log out"
          >
            <RiShutDownLine size={24} />
          </button>
        </div>
      }
      content={
        userProfile ? (
          <div style={{ minWidth: 280 }}>
            <table
              style={{
                fontSize: 13,
                borderCollapse: "collapse",
                width: "100%",
              }}
            >
              <tbody>
                <tr>
                  <td
                    style={{
                      padding: "4px 12px 4px 0",
                      color: "#667781",
                      fontWeight: 500,
                    }}
                  >
                    Name
                  </td>
                  <td style={{ padding: "4px 0" }}>
                    {`${userProfile.FirstName || ""} ${
                      userProfile.LastName || ""
                    }`.trim() || "—"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "4px 12px 4px 0",
                      color: "#667781",
                      fontWeight: 500,
                    }}
                  >
                    Mobile
                  </td>
                  <td style={{ padding: "4px 0" }}>
                    {userProfile.Mobile ? `+${userProfile.Mobile}` : "—"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "4px 12px 4px 0",
                      color: "#667781",
                      fontWeight: 500,
                    }}
                  >
                    Role
                  </td>
                  <td style={{ padding: "4px 0", textTransform: "capitalize" }}>
                    {userProfile.Role || "—"}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      padding: "4px 12px 4px 0",
                      color: "#667781",
                      fontWeight: 500,
                    }}
                  >
                    Status
                  </td>
                  <td style={{ padding: "4px 0" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "1px 8px",
                        borderRadius: 8,
                        fontSize: 12,
                        background:
                          userProfile.ActiveFlag === "active"
                            ? "#e8f5e9"
                            : "#fbe9e7",
                        color:
                          userProfile.ActiveFlag === "active"
                            ? "#2e7d32"
                            : "#c62828",
                      }}
                    >
                      {userProfile.ActiveFlag || "—"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              style={{
                background: "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)",
                borderRadius: 8,
                padding: "12px 16px",
                marginTop: 12,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 13, color: "#667781", fontWeight: 500 }}>
                  Current Plan
                </span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color:
                      planData.plan === "galaxy"
                        ? "#d4af37"
                        : planData.plan === "trial"
                          ? "#667781"
                          : "#00a884",
                  }}
                >
                  {formatPlanLabel(planData.plan)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 13, color: "#667781", fontWeight: 500 }}>
                  Expiry
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111b21" }}>
                  {formatExpiry(planData.expiry)}
                </span>
              </div>
            </div>

            <Popover 
              content="Connect WhatsApp to enable subscription management" 
              trigger={isWabaConnected ? [] : "hover"}
            >
              <div style={{ width: '100%' }}>
                <Button 
                  block 
                  onClick={handleManageSubscription} 
                  style={{ fontSize: 14 }}
                  disabled={!isWabaConnected}
                >
                  Manage Subscription
                </Button>
              </div>
            </Popover>

            <Popover 
              content={
                !isWabaConnected 
                  ? "Connect WhatsApp to enable template management" 
                  : "Subscription required to manage templates"
              } 
              trigger={(isWabaConnected && isSubscriptionValid) ? [] : "hover"}
            >
              <div style={{ width: '100%', marginTop: 8 }}>
                <Button
                  block
                  onClick={handleManageTemplates}
                  style={{ fontSize: 14 }}
                  disabled={!isWabaConnected || !isSubscriptionValid}
                >
                  Manage Templates
                </Button>
              </div>
            </Popover>
          </div>
        ) : (
          <span style={{ color: "#667781" }}>No profile data</span>
        )
      }
    >
      <button
        type="button"
        className="chat-header-icon-btn chat-profile-btn"
        title="Profile"
        aria-label="Profile"
      >
        <RiUser3Line size={24} />
      </button>
    </Popover>
  );
}
